import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMyOutstandingPayments } from "../../redux/userSlice";
import { Helmet } from "react-helmet-async";

let isFetching = false;

const OutstandingPayments = () => {
  const dispatch = useDispatch();
  const { outstandingPayments, loading, error } = useSelector(
    (state) => state.user
  );
  const hasFetchedRef = useRef(false);

  // Filter: "current2" | "specific" | "custom"
  const [filterType, setFilterType] = useState("current2");
  const [specificMonth, setSpecificMonth] = useState(""); // YYYY-MM
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const applyFilter = (params = {}) => {
    isFetching = true;
    dispatch(getMyOutstandingPayments(params)).finally(() => {
      isFetching = false;
    });
  };

  useEffect(() => {
    if (!hasFetchedRef.current && !isFetching && !loading && outstandingPayments === undefined) {
      hasFetchedRef.current = true;
      applyFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilter = () => {
    if (filterType === "current2") {
      applyFilter({});
      return;
    }
    if (filterType === "specific" && specificMonth) {
      const [y, m] = specificMonth.split("-").map(Number);
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0);
      const startDate = start.toISOString().split("T")[0];
      const endDate = end.toISOString().split("T")[0];
      applyFilter({ startDate, endDate });
      return;
    }
    if (filterType === "custom" && customStart && customEnd) {
      applyFilter({ startDate: customStart, endDate: customEnd });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "due":
        return "#dc3545";
      case "paid":
        return "#28a745";
      case "current":
        return "#17a2b8";
      default:
        return "#6c757d";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "due":
        return "⚠️ Due";
      case "paid":
        return "✅ Paid";
      case "current":
        return "📅 Current";
      default:
        return status;
    }
  };

  return (
    <>
      <Helmet>
        <title>Outstanding Payments - RTS Books</title>
      </Helmet>
      <style>{`
        .outstanding-payments-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 50%, #c7d2fe 100%);
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .outstanding-payments-wrapper {
          max-width: 1200px;
          margin: 0 auto;
        }

        .outstanding-payments-header {
          text-align: center;
          margin-bottom: 30px;
          color: #1e40af;
        }

        .outstanding-payments-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          color: #1e40af;
          letter-spacing: -0.5px;
        }

        .outstanding-payments-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(30, 64, 175, 0.08);
          border: 1px solid #e0e7ff;
        }

        .outstanding-payments-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .outstanding-payment-item {
          background: #f8fafc;
          border-radius: 12px;
          padding: 20px;
          border: 2px solid #e2e8f0;
          transition: all 0.3s ease;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 15px;
        }

        .outstanding-payment-item:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
          transform: translateY(-2px);
        }

        .outstanding-payment-item.due {
          border-color: #dc3545;
          background: #fff5f5;
        }

        .outstanding-payment-item.current {
          border-color: #17a2b8;
          background: #f0f9ff;
        }

        .payment-info {
          flex: 1;
          min-width: 200px;
        }

        .payment-month {
          font-size: 1.3rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 5px;
        }

        .payment-year {
          font-size: 0.9rem;
          color: #64748b;
        }

        .payment-amount-section {
          text-align: right;
          min-width: 150px;
        }

        .payment-amount-label {
          font-size: 0.85rem;
          color: #64748b;
          margin-bottom: 5px;
        }

        .payment-amount {
          font-size: 1.8rem;
          font-weight: 700;
          color: #1e40af;
        }

        .payment-amount.due {
          color: #dc3545;
        }

        .payment-status {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          margin-top: 10px;
        }

        .loading-container,
        .error-container,
        .empty-container {
          text-align: center;
          padding: 60px 20px;
        }

        .loading-spinner {
          border: 4px solid #f3f4f6;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-message {
          color: #dc3545;
          font-size: 1.1rem;
          margin-bottom: 20px;
        }

        .empty-message {
          color: #64748b;
          font-size: 1.1rem;
        }

        .summary-card {
          background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
          border-radius: 12px;
          padding: 25px;
          color: white;
          margin-bottom: 30px;
        }

        .summary-title {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 15px;
          opacity: 0.9;
        }

        .summary-total {
          font-size: 2.5rem;
          font-weight: 700;
        }

        .outstanding-filter {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
        }

        .outstanding-filter-row {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          gap: 16px;
        }

        .outstanding-filter-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .outstanding-filter-group label {
          font-weight: 600;
          color: #1e293b;
          font-size: 0.9rem;
        }

        .outstanding-filter-group select,
        .outstanding-filter-group input {
          padding: 10px 12px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 1rem;
          min-width: 160px;
        }

        .outstanding-filter-group input:focus,
        .outstanding-filter-group select:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .btn-apply {
          padding: 10px 20px;
          background: #1e40af;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          font-size: 0.95rem;
        }

        .btn-apply:hover {
          background: #1e3a8a;
        }

        .btn-apply:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .outstanding-payments-container {
            padding: 15px;
          }

          .outstanding-payments-title {
            font-size: 2rem;
          }

          .outstanding-payments-card {
            padding: 20px;
          }

          .outstanding-payment-item {
            flex-direction: column;
            align-items: flex-start;
          }

          .payment-amount-section {
            text-align: left;
            width: 100%;
          }

          .payment-amount {
            font-size: 1.5rem;
          }

          .summary-total {
            font-size: 2rem;
          }
        }
      `}</style>

      <div className="outstanding-payments-container">
        <div className="outstanding-payments-wrapper">
          <div className="outstanding-payments-header">
            <h1 className="outstanding-payments-title">Outstanding Payments</h1>
          </div>

          <div className="outstanding-filter">
            <div className="outstanding-filter-row">
              <div className="outstanding-filter-group">
                <label>Filter</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="current2">Current 2 months (last + current)</option>
                  <option value="specific">Specific month</option>
                  <option value="custom">Custom range</option>
                </select>
              </div>
              {filterType === "specific" && (
                <div className="outstanding-filter-group">
                  <label>Month</label>
                  <input
                    type="month"
                    value={specificMonth}
                    onChange={(e) => setSpecificMonth(e.target.value)}
                  />
                </div>
              )}
              {filterType === "custom" && (
                <>
                  <div className="outstanding-filter-group">
                    <label>From</label>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                    />
                  </div>
                  <div className="outstanding-filter-group">
                    <label>To</label>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                    />
                  </div>
                </>
              )}
              <button
                type="button"
                className="btn-apply"
                onClick={handleApplyFilter}
                disabled={
                  loading ||
                  (filterType === "specific" && !specificMonth) ||
                  (filterType === "custom" && (!customStart || !customEnd))
                }
              >
                Apply
              </button>
            </div>
          </div>

          <div className="outstanding-payments-card">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading outstanding payments...</p>
              </div>
            ) : error ? (
              <div className="error-container">
                <div className="error-message">Error: {error}</div>
                <button
                  onClick={() => dispatch(getMyOutstandingPayments())}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#3b82f6",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "1rem",
                  }}
                >
                  Retry
                </button>
              </div>
            ) : outstandingPayments !== null && outstandingPayments.length > 0 ? (
              <>
                <div className="summary-card">
                  <div className="summary-title">Total Outstanding</div>
                  <div className="summary-total">
                    ₹{outstandingPayments
                      .reduce((sum, payment) => sum + (payment.amount || 0), 0)
                      .toFixed(2)}
                  </div>
                </div>

                <div className="outstanding-payments-list">
                  {outstandingPayments.map((payment, index) => (
                    <div
                      key={payment.periodLabel || `${payment.month}-${payment.year}-${index}`}
                      className={`outstanding-payment-item ${payment.status}`}
                    >
                      <div className="payment-info">
                        <div className="payment-month">
                          {payment.periodLabel ||
                            (payment.monthName || `Month ${payment.month}`)}
                        </div>
                        {!payment.periodLabel && (
                          <div className="payment-year">{payment.year}</div>
                        )}
                        <span
                          className="payment-status"
                          style={{
                            backgroundColor: `${getStatusColor(payment.status)}20`,
                            color: getStatusColor(payment.status),
                          }}
                        >
                          {getStatusBadge(payment.status)}
                        </span>
                      </div>
                      <div className="payment-amount-section">
                        <div className="payment-amount-label">Outstanding Amount</div>
                        <div
                          className={`payment-amount ${payment.status === "due" ? "due" : ""}`}
                        >
                          ₹{parseFloat(payment.amount || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="empty-container">
                <div className="empty-message">
                  No outstanding payments found. All payments are up to date! 🎉
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OutstandingPayments;
